import type { ConnectionProvider, ConnectionStatus, Integration, IntegrationAccount, IntegrationsResponse } from "@/lib/api";
import { S } from "@/lib/strings";

/** Replaces {name} placeholders in a copy string. */
export function fill(template: string, vars: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (match, key: string) => (key in vars ? String(vars[key]) : match));
}

export type ConnectionTab = "budget" | "feed";

export function providerLabel(provider: ConnectionProvider): string {
  switch (provider) {
    case "ynab":
      return S.CONN_SOURCE_YNAB;
    case "lunchmoney":
      return S.CONN_SOURCE_LUNCHMONEY;
    case "simplefin":
      return S.CONN_SOURCE_SIMPLEFIN;
    case "webhook":
      return S.CONN_SOURCE_WEBHOOK;
    case "moneyman":
      return S.CONN_SOURCE_MONEYMAN;
    default:
      return S.CONN_SOURCE_PLAID;
  }
}

export function statusLabel(status: ConnectionStatus): string {
  switch (status) {
    case "pending":
      return S.CONN_STATUS_PENDING;
    case "active":
      return S.CONN_STATUS_ACTIVE;
    case "needs_reauth":
      return S.CONN_STATUS_NEEDS_REAUTH;
    case "error":
      return S.CONN_STATUS_ERROR;
    case "paused":
      return S.CONN_STATUS_PAUSED;
    default:
      return S.CONN_STATUS_DISCONNECTED;
  }
}

/** Badge colour: green when healthy, red when the user must act, grey otherwise. */
export function statusVariant(status: ConnectionStatus): "default" | "secondary" | "destructive" | "outline" {
  switch (status) {
    case "active":
      return "default";
    case "needs_reauth":
    case "error":
      return "destructive";
    case "disconnected":
      return "outline";
    default:
      return "secondary";
  }
}

export function excludeReasonLabel(reason: IntegrationAccount["exclude_reason"]): string {
  switch (reason) {
    case "currency":
      return S.CONN_EXCLUDE_CURRENCY;
    case "possible_duplicate":
      return S.CONN_EXCLUDE_DUPLICATE;
    case "unsupported_type":
      return S.CONN_EXCLUDE_TYPE;
    case "user":
      return S.CONN_EXCLUDE_USER;
    default:
      return "";
  }
}

/** The kind the agency is using now, or null before anything is connected. */
export function activeTab(data: Pick<IntegrationsResponse, "mode"> | undefined): ConnectionTab | null {
  if (!data) return null;
  if (data.mode === "feed") return "feed";
  if (data.mode === "budget") return "budget";
  return null;
}

/** Name of the budgeting app in use, for the "disconnect X first" notice. */
export function budgetAppName(connections: Integration[]): string {
  const app = connections.find((c) => c.kind === "budget" && c.status !== "disconnected");
  return app ? providerLabel(app.provider) : S.CONN_SOURCE_YNAB;
}

/** Why a tab is read-only, or "" when the agency can connect there. */
export function switchNotice(tab: ConnectionTab, data: IntegrationsResponse): string {
  const active = activeTab(data);
  if (!active || active === tab) return "";
  return tab === "feed"
    ? fill(S.CONN_SWITCH_NOTICE_BUDGET, { app: budgetAppName(data.connections) })
    : S.CONN_SWITCH_NOTICE_FEED;
}

/** Live (not disconnected) connections of a kind. */
export function liveConnections(data: IntegrationsResponse | undefined, kind: ConnectionTab): Integration[] {
  return (data?.connections ?? []).filter((c) => c.kind === kind && c.provider !== "ynab" && c.status !== "disconnected");
}

/** Disconnected connections whose history is kept. */
export function pastConnections(data: IntegrationsResponse | undefined): Integration[] {
  return (data?.connections ?? []).filter((c) => c.status === "disconnected" && c.provider !== "ynab");
}

/** Connections that need the user to act, for the reconnect banner. */
export function needsAttention(data: IntegrationsResponse | undefined): Integration[] {
  return (data?.connections ?? []).filter((c) => c.status === "needs_reauth");
}

/** "N of M bank connections used" for the Lead detective tab. */
export function limitText(data: IntegrationsResponse): string {
  return fill(S.CONN_LIMIT, { used: data.feed_count, max: data.policy.max_feeds });
}

export function canAddFeed(data: IntegrationsResponse): boolean {
  return data.is_owner && data.policy.can_connect && data.feed_count < data.policy.max_feeds && activeTab(data) !== "budget";
}

/** A ready-to-paste curl command for the webhook; the key is only ever on screen, never stored. */
export function curlExample(origin: string, endpoint: string, key: string): string {
  const today = new Date().toISOString().slice(0, 10);
  const body = JSON.stringify([
    { account_id: "chk-1", account_name: "Checking", account_type: "checking", currency: "USD", external_id: "tx-1", date: today, amount: "-12.34", payee: "Coffee shop" },
  ]);
  return `curl -X POST ${origin.replace(/\/$/, "")}${endpoint} \\\n  -H "Authorization: Bearer ${key}" \\\n  -H "Content-Type: application/json" \\\n  -d '${body}'`;
}

/** The storage section of Moneyman's configuration that sends its transactions here. */
export function moneymanConfig(origin: string, endpoint: string, key: string): string {
  return JSON.stringify({ storage: { webPost: { url: `${origin.replace(/\/$/, "")}${endpoint}`, authorizationToken: `Bearer ${key}` } } }, null, 2);
}

/** Connections whose data arrives by an ingest key: nothing to sync, and the key can be rotated. */
export function usesIngestKey(provider: ConnectionProvider): boolean {
  return provider === "webhook" || provider === "moneyman";
}

/** Connections a "Sync now" on the dashboard should refresh: pull-based sources that are not waiting for the user. */
export function syncableConnectionIds(data: IntegrationsResponse | undefined): string[] {
  return (data?.connections ?? [])
    .filter((c) => (c.provider === "simplefin" || c.provider === "lunchmoney") && ["active", "error", "pending"].includes(c.status))
    .map((c) => c.id);
}
